'use client';

import { useMemo, useState } from 'react';
import {
  BotIcon,
  ChevronRightIcon,
  HomeIcon,
  LockIcon,
  LucideIcon,
  MessageCircleIcon,
  PhoneCallIcon,
  PieChartIcon,
  RouteIcon,
  SettingsIcon,
  User2Icon,
  Volume2Icon,
} from 'lucide-react';

import { ADMIN_SUB_PAGES } from '@/app/(app)/admin/nav';
import ANALYTICS_SUB_PAGES from '@/app/(app)/analytics/nav';
import { SETTING_SUB_PAGES } from '@/app/(app)/settings/nav';
import { NavUser } from '@/app/(app)/sidebar/nav-user';
import { Link } from '@/components/link';
import Logo from '@/components/logo';
import { useAuthCtx } from '@/components/providers/auth-provider';
import { FeatureFlagName, useFeatureFlagCtx } from '@/components/providers/feature-flag-provider';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { usePathname } from '@/lib/router';

interface MenuSubItem {
  label: string;
  icon: LucideIcon;
  url: string;
}

interface MenuItem {
  label: string;
  icon: LucideIcon;
  url?: string;
  items?: MenuSubItem[];
  permission?: 'superuser' | 'admin';
  featureFlag?: FeatureFlagName;
}

const MENU_ITEMS: MenuItem[] = [
  {
    label: 'Home',
    icon: HomeIcon,
    url: '/',
  },
  {
    label: 'Calls',
    icon: PhoneCallIcon,
    url: '/calls',
    featureFlag: '/voice-agent/calls',
  },
  {
    label: 'Customers',
    icon: User2Icon,
    url: '/customers',
    featureFlag: '/customers',
  },
  {
    label: 'Routes',
    icon: RouteIcon,
    url: '/routes',
    featureFlag: '/settings',
  },
  {
    url: '/voice-agent',
    label: 'Voice Agent',
    icon: BotIcon,
    featureFlag: '/voice-agent/settings',
  },
  {
    url: '/voice-agent/voices',
    label: 'AI Voices',
    icon: Volume2Icon,
    featureFlag: '/voice-agent/voices',
  },
  {
    label: 'Settings',
    icon: SettingsIcon,
    items: SETTING_SUB_PAGES,
    featureFlag: '/settings',
  },
  {
    label: 'Analytics',
    icon: PieChartIcon,
    items: ANALYTICS_SUB_PAGES,
    featureFlag: '/external-calls',
  },
];

export function AppSidebar() {
  const { isSuperuser, isPermissionCovered } = useAuthCtx();
  const { isFeatureEnabled } = useFeatureFlagCtx();
  const { office } = useOfficeCtx();

  const menuItems = useMemo(() => {
    const items = MENU_ITEMS.filter((item) => {
      if (item.featureFlag && !isFeatureEnabled(item.featureFlag)) return false;
      if (item.permission && !isPermissionCovered(item.permission)) return false;
      return true;
    });

    if (isSuperuser) {
      return [
        ...items,
        {
          label: 'Admin',
          icon: LockIcon,
          items: ADMIN_SUB_PAGES,
        },
      ];
    }

    return items;
  }, [isSuperuser, isPermissionCovered, isFeatureEnabled]);

  return (
    <Sidebar variant='inset' className='pt-5'>
      <SidebarHeader className='flex h-6 flex-row items-center justify-between space-x-4 px-3 text-foreground'>
        <div className='flex items-center justify-between space-x-4'>
          <Link href='/'>
            <Logo className='h-5 w-5' />
          </Link>
        </div>
        <div className='text-sm font-semibold'>{office?.name}</div>
      </SidebarHeader>
      <SidebarContent className='mt-7'>
        <SidebarGroup className='list-none space-y-2 px-0'>
          {menuItems.map((item) => (
            <NavMenuItem key={item.url || item.label} item={item} />
          ))}
        </SidebarGroup>
      </SidebarContent>
      <SidebarMenuButton asChild className='feedback-button cursor-pointer'>
        <a>
          <MessageCircleIcon />
          <span>Feedback</span>
        </a>
      </SidebarMenuButton>
      <SidebarFooter className='px-0'>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}

function NavMenuItem({ item }: { item: MenuItem }) {
  const pathname = usePathname();
  const isActive = pathname === item.url;
  const [defaultOpen] = useState(() => {
    if (isActive) return true;
    if (item.items?.some((subItem) => subItem.url === pathname)) return true;
    return false;
  });

  return (
    <Collapsible asChild defaultOpen={defaultOpen}>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip={item.label} isActive={isActive}>
          {item.url ? (
            <Link href={item.url} key={item.label}>
              <item.icon />
              <span>{item.label}</span>
            </Link>
          ) : (
            <CollapsibleTrigger asChild>
              <div key={item.label}>
                <item.icon />
                <span>{item.label}</span>
              </div>
            </CollapsibleTrigger>
          )}
        </SidebarMenuButton>
        {item.items?.length ? (
          <>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction className='data-[state=open]:rotate-90'>
                <ChevronRightIcon />
                <span className='sr-only'>Toggle</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub className='space-y-1 pt-1.5'>
                {item.items.map((subItem) => (
                  <NavMenuSubItem key={subItem.url} item={subItem} isActive={subItem.url === pathname} />
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </>
        ) : null}
      </SidebarMenuItem>
    </Collapsible>
  );
}

function NavMenuSubItem({ item, isActive }: { item: MenuSubItem; isActive: boolean }) {
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={isActive}>
        <Link href={item.url}>
          <item.icon />
          <span>{item.label}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}
